import { computed, effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map, of } from 'rxjs';

import {
  REGISTRATION_BASE_ROUTE,
  RegistrationStepRoute,
} from '@app/features/registration/constants/registration-steps.const';
import { RegistrationSectionStatus } from '@app/features/registration/types';

import { RegistrationStepRegistry } from './registration-step-registry.service';
import { RegistrationStore } from './registration-store.service';
import { RegistrationWizardService } from './registration-wizard.service';

type WizardTarget = RegistrationStepRoute | 'pending';

/**
 * Drives wizard navigation: tracks the current URL, computes the active step
 * index, moves between steps (submitting the active step when needed), and
 * keeps the route in sync with the registration state.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationNavigationService {
  private readonly store = inject(RegistrationStore);
  private readonly wizard = inject(RegistrationWizardService);
  private readonly stepRegistry = inject(RegistrationStepRegistry);
  private readonly router = inject(Router);

  readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  readonly isPendingRoute = computed(() => this.currentUrl().includes('/pending'));

  readonly wizardStepIndex = computed(() => {
    const steps = this.wizard.stepRoutes();

    if (this.isPendingRoute()) {
      return steps.length;
    }

    const url = this.currentUrl();
    const index = steps.findIndex(step => url.includes(step));
    return index === -1 ? 0 : index;
  });

  readonly isPreviousDisabled = computed(() => this.wizardStepIndex() === 0);

  readonly isNextDisabled = computed(() => {
    const currentRoute = this.getCurrentStepRoute(this.currentUrl());

    return currentRoute != null && !this.store.canAdvanceFromSection(currentRoute);
  });

  constructor() {
    effect(() => {
      this.guardLegalEntityRoute(this.currentUrl());
    });
  }

  syncRouteWithRegistrationState(): void {
    if (!this.store.registration()) {
      return;
    }

    if (this.store.isFinished()) {
      void this.router.navigate(['/dashboard']);
      return;
    }

    const url = this.currentUrl();

    if (this.guardLegalEntityRoute(url)) {
      return;
    }

    const target = this.getTargetRoute();

    if (target == null) {
      return;
    }

    if (target === 'pending') {
      if (!url.includes('/pending')) {
        this.navigateTo('pending');
      }
      return;
    }

    if (!url.includes(target)) {
      this.navigateTo(target);
    }
  }

  goToStep(route: RegistrationStepRoute): void {
    if (!this.wizard.isValidStepRoute(route)) {
      return;
    }

    const sectionStatus = this.store.getSectionByRoute(route)?.status;

    if (sectionStatus == null || !this.store.isSectionNavigable(sectionStatus)) {
      return;
    }

    if (this.currentUrl().includes(route)) {
      return;
    }

    this.navigateTo(route);
  }

  goPrevious(): void {
    if (this.isPreviousDisabled()) {
      return;
    }

    const steps = this.wizard.stepRoutes();
    const step = this.wizardStepIndex();
    this.navigateTo(steps[step - 1]);
  }

  goNext(): void {
    if (this.isNextDisabled() || this.stepRegistry.submittingCurrentStep()) {
      return;
    }

    const currentRoute = this.getCurrentStepRoute(this.currentUrl());

    if (currentRoute == null) {
      return;
    }

    if (!this.store.canAdvanceFromSection(currentRoute)) {
      return;
    }

    const advance$ = this.store.canAdvanceWithoutSubmit(currentRoute)
      ? of(true)
      : this.stepRegistry.submitCurrentStep();

    advance$.pipe(filter(success => success)).subscribe(() => {
      this.navigateAfterStepSuccess(currentRoute);
    });
  }

  saveAndExit(): void {
    const exit$ = this.shouldSaveCurrentStepOnExit()
      ? this.stepRegistry.saveCurrentStep()
      : of(true);

    exit$.pipe(filter(success => success)).subscribe(() => {
      void this.router.navigate(['/dashboard']);
    });
  }

  getCurrentStepRoute(url: string): RegistrationStepRoute | null {
    return this.wizard.stepRoutes().find(route => url.includes(route)) ?? null;
  }

  getTargetRoute(): WizardTarget | null {
    if (this.store.isPendingByAdmin()) {
      return 'pending';
    }

    return this.findActionRequiredRoute(0);
  }

  getNextRouteAfter(currentRoute: RegistrationStepRoute): WizardTarget | null {
    if (this.store.isFinished()) {
      return null;
    }

    if (this.store.isPendingByAdmin()) {
      return 'pending';
    }

    const routes = this.wizard.stepRoutes();
    const currentIndex = routes.indexOf(currentRoute);

    if (currentIndex === -1) {
      return null;
    }

    const actionRequired = this.findActionRequiredRoute(currentIndex + 1);

    if (actionRequired != null) {
      return actionRequired;
    }

    if (currentIndex < routes.length - 1) {
      return routes[currentIndex + 1];
    }

    return 'pending';
  }

  private shouldSaveCurrentStepOnExit(): boolean {
    const currentRoute = this.getCurrentStepRoute(this.currentUrl());

    if (currentRoute == null) {
      return false;
    }

    return this.store.getSectionByRoute(currentRoute)?.status === RegistrationSectionStatus.Active;
  }

  private navigateAfterStepSuccess(currentRoute: RegistrationStepRoute): void {
    if (this.store.isFinished()) {
      void this.router.navigate(['/dashboard']);
      return;
    }

    if (this.store.isPendingByAdmin()) {
      this.navigateTo('pending');
      return;
    }

    this.navigateToTarget(this.getNextRouteAfter(currentRoute));
  }

  private navigateToTarget(target: WizardTarget | null): void {
    if (target == null) {
      return;
    }

    this.navigateTo(target);
  }

  private navigateTo(route: WizardTarget): void {
    void this.router.navigate([REGISTRATION_BASE_ROUTE, route]);
  }

  private guardLegalEntityRoute(url: string): boolean {
    if (!this.wizard.isLegalEntity() && url.includes('legal-information')) {
      this.navigateTo('personal-information');
      return true;
    }

    return false;
  }

  private findActionRequiredRoute(fromIndex: number): RegistrationStepRoute | null {
    const routes = this.wizard.stepRoutes();

    for (let index = fromIndex; index < routes.length; index++) {
      const sectionStatus = this.store.getSectionByRoute(routes[index])?.status;

      if (
        sectionStatus === RegistrationSectionStatus.Active
        || sectionStatus === RegistrationSectionStatus.Rejected
      ) {
        return routes[index];
      }
    }

    return null;
  }
}
